import { RelativeTime } from "@/components/RelativeTime";
import { deploymentStatusLabel } from "@/components/StatusBadge";
import { StatusDot } from "@/components/ui";
import { deploymentDot, totalDuration } from "@/lib/deployments";
import type { RecentDeployment } from "@/types/api";

/**
 * One compact line of the global task feed. Shared by the bottom TaskBar and
 * the Datacenter dashboard so both render deployments identically. Uses the
 * full-bleed row of the task panel by default; pass ``light`` for the plain
 * container of the dashboard card. Both sit on light surfaces.
 */
export function TaskRow({
  deployment,
  active = false,
  light = false,
  onSelect,
}: {
  deployment: RecentDeployment;
  active?: boolean;
  light?: boolean;
  onSelect?: (deployment: RecentDeployment) => void;
}) {
  const kindLabel = deployment.kind === "destroy" ? "Destruction" : "Deployment";

  // Both contexts (bottom panel + dashboard card) sit on light surfaces; the
  // ``light`` flag only swaps the container chrome, not the text colours. The
  // current row of the panel keeps its ``text-ink`` (in the base); only the fill is added.
  const base = light
    ? "flex w-full items-center gap-3 rounded px-2 py-1.5 text-left text-xs transition hover:bg-sunken"
    : "flex w-full items-center gap-3 px-3 py-1.5 text-left text-xs text-ink transition hover:bg-sunken-deep focus-visible:outline-offset-[-2px]";
  const activeCls = active ? "bg-brand-100" : "";

  const dot = deploymentDot(deployment.status);
  // The dot is the only mark of the outcome on this line: say it in words too.
  const statusLabel = deploymentStatusLabel(deployment.status);
  const content = (
    <>
      <StatusDot tone={dot.tone} pulse={dot.pulse} size="lg" />
      <span className="w-24 shrink-0 font-medium text-ink">
        {kindLabel}
        <span className="sr-only"> — {statusLabel}</span>
      </span>
      <span className="min-w-0 flex-1 truncate font-mono">
        <span className="text-ink">{deployment.tenantId}</span>
        <span className="text-ink-muted">
          {" / "}
          {deployment.stage}
        </span>
      </span>
      <span className="w-28 shrink-0 text-right text-ink-muted">
        {totalDuration(deployment)}
      </span>
      <span className="w-24 shrink-0 text-right text-ink-muted">
        <RelativeTime epochMs={deployment.triggeredAt} />
      </span>
    </>
  );

  if (!onSelect) {
    return <div className={`${base} ${activeCls}`}>{content}</div>;
  }

  return (
    <button
      type="button"
      className={`${base} ${activeCls}`}
      onClick={() => onSelect(deployment)}
      aria-label={`${kindLabel} ${deployment.tenantId} / ${deployment.stage}, ${statusLabel} — details`}
    >
      {content}
    </button>
  );
}
